import React from 'react';
import { ROICalculation } from '../../../types';

interface ROISectionProps {
  data: ROICalculation;
}

export const ROISection: React.FC<ROISectionProps> = ({ data }) => {
  const { cost_breakdown, savings_projection, payback_period_months, roi_percentage_year_3, recommendation } = data;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getROIColor = (roi: number) => {
    if (roi >= 200) return '#16a34a';
    if (roi >= 100) return '#2563eb';
    if (roi >= 50) return '#ca8a04';
    return '#dc2626';
  };

  return (
    <div style={{ marginBottom: '3rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
          ROI Analysis
        </h2>
        <p style={{ color: '#6b7280' }}>
          Financial impact and return on investment projection
        </p>
      </div>

      {/* Key Metrics Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#2563eb', fontWeight: '500', marginBottom: '0.25rem' }}>Investment</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1e3a8a' }}>
            {formatCurrency(cost_breakdown.total_cost)}
          </p>
          <p style={{ fontSize: '0.75rem', color: '#1e40af', marginTop: '0.25rem' }}>
            {cost_breakdown.total_hours} hours
          </p>
        </div>

        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '12px', padding: '1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#16a34a', fontWeight: '500', marginBottom: '0.25rem' }}>Annual Savings</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#14532d' }}>
            {formatCurrency(savings_projection.total_annual_savings)}
          </p>
          <p style={{ fontSize: '0.75rem', color: '#15803d', marginTop: '0.25rem' }}>
            {formatCurrency(savings_projection.total_monthly_savings)}/month
          </p>
        </div>

        <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '12px', padding: '1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#9333ea', fontWeight: '500', marginBottom: '0.25rem' }}>Payback Period</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#581c87' }}>
            {payback_period_months.toFixed(1)}
          </p>
          <p style={{ fontSize: '0.75rem', color: '#7e22ce', marginTop: '0.25rem' }}>months</p>
        </div>

        <div style={{ background: 'linear-gradient(to bottom right, #fef3c7, #fed7aa)', border: '1px solid #fdba74', borderRadius: '12px', padding: '1.5rem' }}>
          <p style={{ fontSize: '0.875rem', color: '#c2410c', fontWeight: '500', marginBottom: '0.25rem' }}>3-Year ROI</p>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: getROIColor(roi_percentage_year_3) }}>
            {roi_percentage_year_3.toFixed(0)}%
          </p>
          <p style={{ fontSize: '0.75rem', color: '#ea580c', marginTop: '0.25rem' }}>return</p>
        </div>
      </div>

      {/* Recommendation Banner */}
      <div style={{ 
        background: 'linear-gradient(to right, #3b82f6, #9333ea)', 
        color: '#ffffff', 
        borderRadius: '12px', 
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginRight: '1rem' }}>💡</div>
          <div>
            <p style={{ fontSize: '0.875rem', fontWeight: '500', opacity: 0.9, marginBottom: '0.25rem' }}>Recommendation</p>
            <p style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{recommendation}</p>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>Cost Breakdown</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {[
            { label: 'Discovery', hours: cost_breakdown.discovery_hours },
            { label: 'Design', hours: cost_breakdown.design_hours },
            { label: 'Development', hours: cost_breakdown.development_hours },
            { label: 'Testing', hours: cost_breakdown.testing_hours },
            { label: 'Deployment', hours: cost_breakdown.deployment_hours },
            { label: 'Training', hours: cost_breakdown.training_hours },
          ].map((phase) => (
            <div key={phase.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#374151' }}>{phase.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{phase.hours}h</span>
                <span style={{ fontWeight: '600', color: '#111827', width: '100px', textAlign: 'right' }}>
                  {formatCurrency(phase.hours * cost_breakdown.hourly_rate)}
                </span>
              </div>
            </div>
          ))}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.125rem' }}>
              <span style={{ color: '#111827' }}>Total</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ color: '#6b7280' }}>{cost_breakdown.total_hours}h</span>
                <span style={{ color: '#2563eb', width: '100px', textAlign: 'right' }}>
                  {formatCurrency(cost_breakdown.total_cost)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {(cost_breakdown.infrastructure_cost || cost_breakdown.third_party_tools) && (
          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem' }}>Additional Costs</h4>
            {cost_breakdown.infrastructure_cost && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                <span style={{ color: '#6b7280' }}>Infrastructure (monthly)</span>
                <span style={{ fontWeight: '500' }}>{formatCurrency(cost_breakdown.infrastructure_cost)}</span>
              </div>
            )}
            {cost_breakdown.third_party_tools && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span style={{ color: '#6b7280' }}>Third-party Tools (monthly)</span>
                <span style={{ fontWeight: '500' }}>{formatCurrency(cost_breakdown.third_party_tools)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Savings Breakdown */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>Expected Savings</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ borderLeft: '4px solid #10b981', paddingLeft: '1rem' }}>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Time Savings</p>
            <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a' }}>
              {formatCurrency(savings_projection.monthly_time_savings_usd)}/month
            </p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {savings_projection.hours_saved_monthly} hours saved monthly
            </p>
          </div>

          {savings_projection.error_reduction_percentage && savings_projection.monthly_error_savings_usd && (
            <div style={{ borderLeft: '4px solid #2563eb', paddingLeft: '1rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Error Reduction</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563eb' }}>
                {formatCurrency(savings_projection.monthly_error_savings_usd)}/month
              </p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                {savings_projection.error_reduction_percentage}% fewer errors
              </p>
            </div>
          )}

          <div style={{ background: '#f9fafb', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: '600', color: '#111827' }}>Total Monthly Savings</span>
              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a' }}>
                {formatCurrency(savings_projection.total_monthly_savings)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Annual Savings</span>
              <span style={{ fontSize: '1.125rem', fontWeight: '600', color: '#15803d' }}>
                {formatCurrency(savings_projection.total_annual_savings)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ROI Timeline */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '1rem' }}>ROI Over Time</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: '#fef3c7', borderRadius: '8px' }}>
            <div>
              <p style={{ fontWeight: '600', color: '#111827' }}>Year 1</p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Net: {formatCurrency(data.npv_year_1)}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: getROIColor(data.roi_percentage_year_1) }}>
                {data.roi_percentage_year_1.toFixed(1)}%
              </p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>ROI</p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: '#dcfce7', borderRadius: '8px' }}>
            <div>
              <p style={{ fontWeight: '600', color: '#111827' }}>Year 3</p>
              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Net: {formatCurrency(data.npv_year_3)}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: getROIColor(roi_percentage_year_3) }}>
                {roi_percentage_year_3.toFixed(0)}%
              </p>
              <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>ROI</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

